import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Zap, PlayCircle, PhoneOff, Shield, FileText, Flame, CalendarClock,
  Loader2, RefreshCw, AlertTriangle, Settings2, Info, CheckCircle2, TestTube2,
} from 'lucide-react';

const RULES = [
  {
    key: 'no_answer', icon: PhoneOff, color: '#6B7280',
    title: 'Pas de réponse x3',
    description: 'Prospect injoignable plusieurs fois → relance automatique à J+N',
    enabledKey: 'automation_no_answer_enabled',
    fields: [
      { key: 'automation_no_answer_attempts', label: 'Tentatives min', suffix: '' },
      { key: 'automation_no_answer_delay_days', label: 'Rappel à J+', suffix: 'j' },
    ],
  },
  {
    key: 'lost_competitor', icon: Shield, color: '#EF4444',
    title: 'Perdu face concurrent',
    description: 'Statut "Refus" + prestataire connu → rappel à 6 ou 12 mois',
    enabledKey: 'automation_lost_competitor_enabled',
    fields: [
      { key: 'automation_lost_competitor_delay_days', label: 'Rappel à J+', suffix: 'j' },
    ],
  },
  {
    key: 'quote_followup', icon: FileText, color: '#F59E0B',
    title: 'Devis envoyé sans retour',
    description: 'Statut "Devis envoyé" sans mise à jour depuis N jours → alerte + relance',
    enabledKey: 'automation_quote_followup_enabled',
    fields: [
      { key: 'automation_quote_followup_days', label: 'Délai max', suffix: 'j' },
    ],
  },
  {
    key: 'hot_prospect', icon: Flame, color: '#F97316',
    title: 'Prospect chaud non traité',
    description: 'Intéressé / en négociation sans action depuis N heures → alerte rouge',
    enabledKey: 'automation_hot_prospect_enabled',
    fields: [
      { key: 'automation_hot_prospect_hours', label: 'Seuil', suffix: 'h' },
    ],
  },
  {
    key: 'rdv_reminder', icon: CalendarClock, color: '#10B981',
    title: 'Rappel RDV J-1',
    description: 'RDV le lendemain → alerte interne la veille',
    enabledKey: 'automation_rdv_reminder_enabled',
    fields: [],
  },
];

export default function AutomationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/automations/status');
      setStatus(data);
    } catch (e) {
      toast.error("Erreur chargement automatisations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSetting = async (key, value) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.put('/settings', { settings: { [key]: String(value) } });
      setStatus(prev => prev ? { ...prev, settings: { ...prev.settings, [key]: String(value) } } : prev);
      toast.success("Paramètre enregistré");
    } catch (e) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const runAutomations = async (dryRun = false) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/automations/run?dry_run=${dryRun}`);
      toast.success(`${dryRun ? '🔍 Simulation' : '✅ Exécution'} : ${data.total_triggered} action(s) ${dryRun ? 'potentielles' : 'déclenchées'} en ${data.duration_seconds}s`);
      if (!dryRun) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur exécution");
    } finally {
      setRunning(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="automations-unauthorized">
        <Card>
          <CardContent className="p-6 flex items-center gap-3 text-[#71717A]">
            <AlertTriangle className="w-5 h-5" />
            <p>Seuls les administrateurs peuvent accéder aux automatisations.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || !status) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#F97316]" /></div>;
  }

  const settings = status.settings || {};
  const schedulerEnabled = (settings.automation_scheduler_enabled || 'true').toLowerCase() === 'true';
  const lastRun = status.last_run ? new Date(status.last_run).toLocaleString('fr-FR') : '— jamais —';
  const totalLastRun = status.last_summary?.total_triggered ?? 0;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="automations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#F97316]" />Automatisations commerciales
          </h1>
          <p className="text-xs text-[#71717A]">Règles automatiques qui tournent toutes les heures. Modifiez les paramètres et testez en simulation avant d'activer.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => runAutomations(true)} disabled={running} data-testid="dry-run-btn">
            <TestTube2 className="w-4 h-4 mr-1" />Simuler
          </Button>
          <Button onClick={() => runAutomations(false)} disabled={running} className="bg-[#F97316] hover:bg-[#EA580C]" data-testid="run-now-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
            Exécuter maintenant
          </Button>
        </div>
      </div>

      {/* Scheduler status */}
      <Card className="border-l-4 border-l-[#F97316]">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Settings2 className="w-5 h-5 text-[#71717A]" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">Scheduler automatique</span>
                {schedulerEnabled ? <Badge className="bg-[#10B981]">Actif</Badge> : <Badge variant="outline">Désactivé</Badge>}
              </div>
              <div className="text-xs text-[#71717A] mt-0.5">
                Dernière exécution : <span className="font-mono">{lastRun}</span>
                {totalLastRun > 0 && <span className="text-[#F97316] ml-2">({totalLastRun} action{totalLastRun > 1 ? 's' : ''})</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="sched-toggle" className="text-xs">Exécution horaire</Label>
            <Switch
              id="sched-toggle"
              checked={schedulerEnabled}
              onCheckedChange={v => saveSetting('automation_scheduler_enabled', v)}
              data-testid="scheduler-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Rules */}
      <div className="grid md:grid-cols-2 gap-4">
        {RULES.map(rule => {
          const Icon = rule.icon;
          const enabled = (settings[rule.enabledKey] || 'true').toLowerCase() === 'true';
          return (
            <Card key={rule.key} className={`${enabled ? '' : 'opacity-70'}`} data-testid={`rule-card-${rule.key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${rule.color}15`, color: rule.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-sm">{rule.title}</CardTitle>
                      <CardDescription className="text-xs mt-0.5">{rule.description}</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={v => saveSetting(rule.enabledKey, v)}
                    disabled={saving[rule.enabledKey]}
                    data-testid={`rule-toggle-${rule.key}`}
                  />
                </div>
              </CardHeader>
              {rule.fields.length > 0 && (
                <CardContent className="pt-0 space-y-2">
                  {rule.fields.map(f => (
                    <div key={f.key} className="flex items-center gap-2">
                      <Label className="text-xs text-[#71717A] flex-1">{f.label}</Label>
                      <Input
                        type="number"
                        value={settings[f.key] || ''}
                        onChange={e => {
                          setStatus(prev => ({ ...prev, settings: { ...prev.settings, [f.key]: e.target.value } }));
                        }}
                        onBlur={e => saveSetting(f.key, e.target.value)}
                        className="w-20 h-8 text-sm"
                        disabled={!enabled}
                        data-testid={`rule-field-${f.key}`}
                      />
                      {f.suffix && <span className="text-xs text-[#71717A] w-6">{f.suffix}</span>}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Last run summary */}
      {status.last_summary?.rules && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Info className="w-4 h-4 text-[#71717A]" />Résumé dernière exécution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-3 text-center">
              {RULES.map(rule => {
                const r = status.last_summary.rules[rule.key];
                const count = r?.triggered ?? 0;
                const skipped = r?.skipped;
                return (
                  <div key={rule.key} className={`p-3 rounded-lg ${count > 0 ? 'bg-[#FFF7ED] border border-[#F97316]' : 'bg-[#F4F4F5]'}`}>
                    <div className="text-xs text-[#71717A] mb-1 truncate">{rule.title}</div>
                    <div className="text-2xl font-bold" style={{ color: count > 0 ? '#F97316' : '#9CA3AF' }}>{skipped ? '—' : count}</div>
                    {skipped && <div className="text-[10px] text-[#71717A]">désactivé</div>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
